import React from "react";
import {
  Smartphone,
  Monitor,
  Tablet,
  Apple,
  ChromeIcon,
  Laptop,
} from "lucide-react";

interface DeviceIconProps {
  deviceType?: "mobile" | "tablet" | "desktop";
  os?: string;
  className?: string;
  showLabel?: boolean;
}

export const DeviceIcon: React.FC<DeviceIconProps> = ({
  deviceType = "desktop",
  os = "Unknown",
  className = "h-4 w-4",
  showLabel = false,
}) => {
  const getDeviceIcon = () => {
    const osLower = os.toLowerCase();

    // iOS devices
    if (
      osLower.includes("ios") ||
      osLower.includes("iphone") ||
      osLower.includes("ipad")
    ) {
      return {
        icon: <Apple className={className} />,
        label: "iOS",
        color: "text-gray-800",
      };
    }

    // Android devices
    if (osLower.includes("android")) {
      return {
        icon: (
          <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.523 15.3414c-.5665 0-1.0253-.4588-1.0253-1.0253s.4588-1.0253 1.0253-1.0253 1.0253.4588 1.0253 1.0253-.4588 1.0253-1.0253 1.0253zm-11.046 0c-.5665 0-1.0253-.4588-1.0253-1.0253s.4588-1.0253 1.0253-1.0253 1.0253.4588 1.0253 1.0253-.4588 1.0253-1.0253 1.0253zm11.405-6.02l1.395-2.4157c.077-.1326.0315-.3017-.1011-.3787-.1326-.077-.3017-.0315-.3787.1011L17.4042 8.8495c-.6897-.3157-1.4612-.4912-2.4042-.4912s-1.7145.1755-2.4042.4912L11.208 6.6671c-.077-.1326-.2461-.1781-.3787-.1011-.1326.077-.1781.2461-.1011.3787l1.395 2.4157C10.0618 10.2971 8.535 11.7842 8.535 13.6253v.8439h6.93v-.8439c0-1.8411-1.5268-3.3282-3.465-4.2839zM24 12c0 6.6274-5.3726 12-12 12S0 18.6274 0 12 5.3726 0 12 0s12 5.3726 12 12z" />
          </svg>
        ),
        label: "Android",
        color: "text-green-600",
      };
    }

    // macOS
    if (osLower.includes("mac") || osLower.includes("darwin")) {
      return {
        icon: <Apple className={className} />,
        label: "macOS",
        color: "text-gray-700",
      };
    }

    // Windows
    if (osLower.includes("windows") || osLower.includes("win")) {
      return {
        icon: (
          <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-13.051-1.351" />
          </svg>
        ),
        label: "Windows",
        color: "text-blue-600",
      };
    }

    // Linux
    if (osLower.includes("linux")) {
      return {
        icon: <Monitor className={className} />,
        label: "Linux",
        color: "text-yellow-600",
      };
    }

    // Default based on device type
    switch (deviceType) {
      case "mobile":
        return {
          icon: <Smartphone className={className} />,
          label: "Mobile",
          color: "text-gray-600",
        };
      case "tablet":
        return {
          icon: <Tablet className={className} />,
          label: "Tablet",
          color: "text-gray-600",
        };
      default:
        return {
          icon: <Monitor className={className} />,
          label: "Desktop",
          color: "text-gray-600",
        };
    }
  };

  const { icon, label, color } = getDeviceIcon();

  if (showLabel) {
    return (
      <div className="flex items-center gap-1">
        <span className={color}>{icon}</span>
        <span className="text-xs text-gray-600">{label}</span>
      </div>
    );
  }

  return (
    <span className={color} title={`${label} (${os})`}>
      {icon}
    </span>
  );
};

// Helper function to get device breakdown for a source
export const getDeviceBreakdown = (sessions: any[], utmSource?: string) => {
  const filteredSessions = utmSource
    ? sessions.filter((session) => session.utmParams?.source === utmSource)
    : sessions;

  const deviceCounts = filteredSessions.reduce(
    (acc, session) => {
      const deviceKey = `${session.device.type}_${session.device.os}`;
      acc[deviceKey] = (acc[deviceKey] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return Object.entries(deviceCounts)
    .map(([deviceKey, count]: [string, number]) => {
      const [type, os] = deviceKey.split("_");
      return {
        type: type as "mobile" | "tablet" | "desktop",
        os,
        count,
      };
    })
    .sort((a, b) => (b.count as number) - (a.count as number));
};
